import { ApiError, apiRequest } from "./apiClient";
import type {
  GameRuleGeneratedDocumentApproveResult,
  GameRuleGeneratedDocumentDetail,
  GameRuleGeneratedDocumentSaveResult,
  GameRuleGeneratedListRow,
} from "../types/gameRuleGenerated";

type KbGeneratedDocumentListApiItem = {
  readonly id: string;
  readonly name: string;
  readonly file_name: string;
  readonly generated_at: string;
  readonly section_count: number;
  readonly reviewer_email?: string | null;
  readonly status: string;
};

type KbGeneratedDocumentListResponse = {
  readonly success: boolean;
  readonly data: readonly KbGeneratedDocumentListApiItem[];
  readonly meta: { readonly total: number; readonly page: number; readonly limit: number };
};

type KbGeneratedDocumentDetailApi = {
  readonly id: string;
  readonly name: string;
  readonly file_name: string;
  readonly status: string;
  readonly reviewer_email?: string | null;
  readonly reviewer_notes?: string | null;
  readonly review_comment?: string | null;
  readonly reviewed_by?: string | null;
  readonly reviewed_at?: string | null;
  readonly generated_at: string;
  readonly section_count: number;
  readonly sections?: readonly string[] | null;
  readonly stored_file_id: string;
  readonly document_web_url?: string | null;
  readonly can_review?: boolean;
  readonly review_locked_message?: string | null;
};

type KbGeneratedDocumentDetailResponse = {
  readonly success: boolean;
  readonly data: KbGeneratedDocumentDetailApi;
};

type KbGeneratedDocumentSaveApi = {
  readonly id: string;
  readonly stored_file_id: string;
  readonly file_name: string;
  readonly status: string;
  readonly size_bytes?: number | null;
  readonly document_web_url?: string | null;
  readonly updated_at?: string | null;
};

type KbGeneratedDocumentSaveResponse = {
  readonly success: boolean;
  readonly data: KbGeneratedDocumentSaveApi;
};

type KbGeneratedDocumentApproveApi = {
  readonly id: string;
  readonly status: string;
  readonly reviewed_by: string;
  readonly reviewed_at: string;
  readonly review_comment?: string | null;
};

type KbGeneratedDocumentApproveResponse = {
  readonly success: boolean;
  readonly data: KbGeneratedDocumentApproveApi;
};

type KbGeneratedDocumentSummaryResponse = {
  readonly success: boolean;
  readonly data: { readonly total: number; readonly label: string };
};

async function parseDetail(response: Response): Promise<string> {
  const raw = await response.text();
  try {
    const o = JSON.parse(raw) as { detail?: string };
    return typeof o.detail === "string" ? o.detail : raw || `HTTP ${response.status}`;
  } catch {
    return raw || `HTTP ${response.status}`;
  }
}

const mapListRow = (row: KbGeneratedDocumentListApiItem): GameRuleGeneratedListRow => ({
  id: String(row.id),
  name: row.name,
  fileName: row.file_name,
  generatedAt: row.generated_at,
  sectionCount: row.section_count ?? 0,
  reviewerEmail: row.reviewer_email ?? null,
  status: row.status,
});

export const mapGeneratedDocumentDetail = (
  d: KbGeneratedDocumentDetailApi,
): GameRuleGeneratedDocumentDetail => ({
  id: String(d.id),
  name: d.name,
  fileName: d.file_name,
  status: d.status,
  reviewerEmail: d.reviewer_email ?? null,
  reviewerNotes: d.reviewer_notes ?? null,
  reviewComment: d.review_comment ?? null,
  reviewedBy: d.reviewed_by ?? null,
  reviewedAt: d.reviewed_at ?? null,
  generatedAt: d.generated_at,
  sectionCount: d.section_count ?? 0,
  sections: Array.isArray(d.sections) ? d.sections.map(String) : [],
  storedFileId: String(d.stored_file_id),
  documentWebUrl: d.document_web_url ?? null,
  canReview: Boolean(d.can_review),
  reviewLockedMessage: d.review_locked_message ?? null,
});

export const getKbGeneratedDocumentsSummary = async (): Promise<number> => {
  const response = await apiRequest("/game-rule/generated-documents/summary", undefined);
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as KbGeneratedDocumentSummaryResponse;
  return typeof json.data?.total === "number" ? json.data.total : 0;
};

export const listGameRuleGeneratedDocuments = async (
  page = 1,
  limit = 50,
  search?: string,
  status = "all",
): Promise<{ readonly items: GameRuleGeneratedListRow[]; readonly total: number }> => {
  const q = new URLSearchParams();
  q.set("page", String(page));
  q.set("limit", String(limit));
  q.set("status", status);
  if (search?.trim()) {
    q.set("search", search.trim());
  }
  const response = await apiRequest(`/game-rule/generated-documents?${q.toString()}`, undefined);
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as KbGeneratedDocumentListResponse;
  const rows = Array.isArray(json.data) ? json.data : [];
  return {
    items: rows.map(mapListRow),
    total: json.meta?.total ?? rows.length,
  };
};

export const getGameRuleGeneratedDocumentDetail = async (
  documentId: string,
): Promise<GameRuleGeneratedDocumentDetail> => {
  const response = await apiRequest(
    `/game-rule/generated-documents/${encodeURIComponent(documentId)}`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as KbGeneratedDocumentDetailResponse;
  if (!json.data?.stored_file_id) {
    throw new ApiError("Document has no stored file.", 404);
  }
  return mapGeneratedDocumentDetail(json.data);
};

export const getKbGeneratedDocumentStoredFileId = async (documentId: string): Promise<string> => {
  const detail = await getGameRuleGeneratedDocumentDetail(documentId);
  return detail.storedFileId;
};

export const putGameRuleGeneratedDocumentFile = async (
  documentId: string,
  blob: Blob,
  fileName = "document.docx",
): Promise<GameRuleGeneratedDocumentSaveResult> => {
  const fd = new FormData();
  fd.append("file", blob, fileName);
  const response = await apiRequest(
    `/game-rule/generated-documents/${encodeURIComponent(documentId)}/document`,
    { method: "PUT", body: fd },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as KbGeneratedDocumentSaveResponse;
  const d = json.data;
  return {
    id: String(d.id),
    storedFileId: String(d.stored_file_id),
    fileName: d.file_name,
    status: d.status,
    sizeBytes: d.size_bytes ?? null,
    documentWebUrl: d.document_web_url ?? null,
    updatedAt: d.updated_at ?? null,
  };
};

export const approveGameRuleGeneratedDocumentReview = async (
  documentId: string,
  comment?: string | null,
): Promise<GameRuleGeneratedDocumentApproveResult> => {
  const response = await apiRequest(
    `/game-rule/generated-documents/${encodeURIComponent(documentId)}/review`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "approve",
        comment: comment?.trim() ? comment.trim() : null,
      }),
    },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as KbGeneratedDocumentApproveResponse;
  const d = json.data;
  return {
    id: String(d.id),
    status: d.status,
    reviewedBy: d.reviewed_by,
    reviewedAt: d.reviewed_at,
    reviewComment: d.review_comment ?? null,
  };
};
